























































import { getSafePropertyChain } from '@empathy/get-safe-property-chain';
import { VNode } from 'vue';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Dictionary } from '../../utils';
import AddClassToSlot from '../pure/add-class-to-slot';

@Component({
  components: {
    AddClassToSlot
  }
})
export default class TabbedPanel extends Vue {
  @Prop({ default: false })
  fullscreen: boolean;

  @Prop({ default: true })
  draggable: boolean;

  @Prop({ default: null })
  initialTabIndex: number;

  @Prop({ default: false })
  renderToggleTabsInFooter: boolean;

  @Prop({ default: true })
  renderToggleTabsInHeader: boolean;

  @Prop({ default: true })
  allowTabDeselect: boolean;

  @Prop()
  toggleTabsMessagesKey: string;

  activeTab = this.initialTabIndex;
  startDrag = 0;
  endDrag = 0;
  snapping = false;
  dragging = false;

  lastAnimationFrame: number;
  $refs: {
    panel: HTMLElement;
  };

  get cssClasses() {
    return {
      'ebx-tabbed-panel--is-collapsed': this.activeTab === null,
      'ebx-tabbed-panel--is-dragging': this.dragging,
      'ebx-tabbed-panel--is-snapping': this.snapping,
      'ebx-tabbed-panel--is-draggable': this.draggable,
      'ebx-tabbed-panel--is-not-draggable': !this.draggable,
      'ebx-tabbed-panel--is-full-screen': this.fullscreen
    };
  }

  get dragDistance() {
    return this.startDrag - this.endDrag;
  }

  get dragX() {
    return this.activeTab === null ?
      '0' :
      `calc(${ -this.activeTab / this.$slots.default.length * 100 }% - ${ this.startDrag - this.endDrag }px)`;
  }

  get tabsMessages() {
    return this.toggleTabsMessagesKey != null
      ? getSafePropertyChain(this.$x.messages, this.toggleTabsMessagesKey)
      : null;
  }

  @Watch('initialTabIndex')
  updateActiveTab(newActiveTab: number) {
    this.activeTab = newActiveTab;
  }

  addTabClass(vnode: VNode, index: number): Dictionary<boolean> {
    return this.activeTab !== null
      ? {
        'ebx-tabbed-panel__tab--is-active': index === this.activeTab,
        'ebx-tabbed-panel__tab--is-previous': index === this.activeTab - 1,
        'ebx-tabbed-panel__tab--is-next': index === this.activeTab + 1
      }
      : {};
  }

  toggleTab(newActiveTabIndex: number) {
    if (this.activeTab === newActiveTabIndex) {
      if (this.allowTabDeselect) {
        this.activeTab = null;
      }
    } else {
      if (this.$slots.default[newActiveTabIndex]) {
        if (this.activeTab !== null && newActiveTabIndex !== null) {
          this.snapping = true;
        }
        this.activeTab = newActiveTabIndex;
      }
    }
  }

  enableTouchDragging({ touches }: TouchEvent) {
    this.enableDragging(touches[0]);
  }

  enableDragging({ clientX }: (MouseEvent | Touch)) {
    if (!this.dragging && !this.snapping) {
      this.startDrag = this.endDrag = clientX;
      this.dragging = true;
    }
  }

  touchDrag({ touches }: TouchEvent) {
    this.drag(touches[0]);
  }

  drag({ clientX }: (MouseEvent | Touch)) {
    if (this.dragging) {
      this.lastAnimationFrame = requestAnimationFrame(() => {
        if (!this.snapping) { // Ñapa here to prevent multiple unprocessed animation frames in iOS
          this.endDrag = clientX;
        }
      });
    }
  }

  snap() {
    if (this.dragging && this.dragDistance !== 0) {
      if (this.lastAnimationFrame) {
        cancelAnimationFrame(this.lastAnimationFrame);
      }
      this.dragging = false;
      this.snapping = true;

      const width = this.$refs.panel.offsetWidth;
      const draggingThreshold = width / 4;
      const absDraggedDistance = Math.abs(this.dragDistance);
      if (absDraggedDistance > draggingThreshold) {
        if (this.dragDistance > 0) { // Used dragged from RIGHT to LEFT
          this.activeTab = this.nextTab();
        } else { // User dragged from LEFT to RIGHT;
          this.activeTab = this.previousTab();
        }
      }
    }
    this.resetDrag();
  }

  nextTab() {
    return Math.min(this.activeTab + 1, this.$slots.default.length - 1);
  }

  previousTab() {
    return Math.max(this.activeTab - 1, 0);
  }

  resetDrag() {
    this.startDrag = this.endDrag = 0;
    this.dragging = false;
  }

  endSnapping() {
    if (this.snapping) {
      this.snapping = false;
    }
  }
}
